import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { getArticleBySlug, allArticles } from '@/mocks/articles';
import type { ArticleBlock } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ArticleJsonLd, BreadcrumbJsonLd, FAQJsonLd } from '@/components/feature/JsonLd';
import { SITE_URL } from '@/constants/site';

function ArticleContent({ slug }: { slug: string }) {
  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center px-4">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-secondary-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-secondary-500"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-500 mb-6">
              The article you are looking for does not exist or may have been moved. Browse our available guides below.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Back to Homepage
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const renderBlock = (block: ArticleBlock, index: number) => {
    switch (block.type) {
      case 'heading':
        return (
          <h2
            key={index}
            id={`section-${index}`}
            className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-12 mb-4"
          >
            {block.text}
          </h2>
        );

      case 'subheading':
        return (
          <h3
            key={index}
            className="font-heading text-lg md:text-xl font-semibold text-foreground-800 mt-8 mb-3"
          >
            {block.text}
          </h3>
        );

      case 'paragraph':
        return (
          <p
            key={index}
            className="text-sm md:text-base text-foreground-600 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: block.text || '' }}
          />
        );

      case 'list':
        return (
          <ul key={index} className="space-y-2.5 ml-5">
            {block.items?.map((item, i) => (
              <li key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed flex gap-2">
                <span className="text-primary-500 mt-0.5 flex-shrink-0">·</span>
                <span dangerouslySetInnerHTML={{ __html: item }} />
              </li>
            ))}
          </ul>
        );

      case 'orderedList':
        return (
          <ol key={index} className="space-y-3 ml-5 list-decimal">
            {block.items?.map((item, i) => (
              <li
                key={i}
                className="text-sm md:text-base text-foreground-600 leading-relaxed pl-1"
                dangerouslySetInnerHTML={{ __html: item }}
              />
            ))}
          </ol>
        );

      case 'table':
        return (
          <div key={index} className="overflow-x-auto rounded-xl border border-background-200">
            <table className="w-full">
              <thead>
                <tr className="bg-background-100">
                  {block.headers?.map((header, i) => (
                    <th
                      key={i}
                      className={`px-4 py-3 text-sm font-semibold text-foreground-800 whitespace-nowrap ${i === 0 ? 'text-left' : i === (block.headers ? block.headers.length - 1 : 0) ? 'text-left text-primary-600' : 'text-left'}`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows?.map((row, ri) => (
                  <tr key={ri} className="border-t border-background-200">
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={`px-4 py-3 text-sm whitespace-nowrap ${ci === 0 ? 'text-foreground-700 font-medium' : ci === row.length - 1 ? 'text-primary-600 font-medium' : 'text-foreground-500'}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'faq':
        return (
          <div key={index} className="space-y-3 mt-6">
            {block.items?.map((faq, fi) => (
              <details
                key={fi}
                className="group bg-background-100 rounded-xl border border-background-200 overflow-hidden cursor-pointer"
              >
                <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-800 list-none">
                  {faq.question}
                  <i className="ri-add-line group-open:hidden w-5 h-5 flex items-center justify-center text-foreground-400 flex-shrink-0 ml-3"></i>
                  <i className="ri-subtract-line hidden group-open:flex w-5 h-5 items-center justify-center text-foreground-400 flex-shrink-0 ml-3"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <ArticleJsonLd
        headline={article.title}
        description={article.metaDescription}
        image={article.heroImageUrl}
        datePublished={article.publishedDate}
        dateModified={article.modifiedDate}
        url={`${SITE_URL}/article/${article.slug}`}
        publisherName="Vigorbuy Spread"
      />

      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Guides', url: `${SITE_URL}/article/${article.slug}` },
          { name: article.category, url: `${SITE_URL}/article/${article.slug}` },
        ]}
      />

      {article.content
        .filter((block) => block.type === 'faq')
        .flatMap((block) => block.items || [])
        .length > 0 && (
        <FAQJsonLd
          items={article.content
            .filter((block) => block.type === 'faq')
            .flatMap((block) => (block.items || []).map((item) => ({
              question: item.question,
              answer: item.answer,
            })))}
        />
      )}

      <article className="pt-20">
        <header className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 text-xs text-foreground-400 mb-4">
              <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">
                Home
              </Link>
              <span>/</span>
              <span>Guides</span>
              <span>/</span>
              <span className="text-foreground-600">{article.category}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-8">
              <span>Published: {article.publishedDate}</span>
              <span>·</span>
              <span>Updated: {article.modifiedDate}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>

            <div className="w-full h-[240px] md:h-[360px] rounded-xl overflow-hidden mb-8">
              <img
                src={article.heroImageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed border-l-2 border-primary-400 pl-4 py-1 italic">
              {article.excerpt}
            </p>
          </div>
        </header>

        <nav className="w-full px-4 md:px-6 py-8 bg-background-50 border-b border-background-200">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-sm font-semibold text-foreground-800 uppercase tracking-wider mb-4">Table of Contents</h2>
            <ul className="space-y-2">
              {article.content
                .map((block, index) => ({ block, index }))
                .filter(({ block }) => block.type === 'heading')
                .map(({ block, index }) => (
                  <li key={index}>
                    <a
                      href={`#section-${index}`}
                      className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer"
                    >
                      {block.text}
                    </a>
                  </li>
                ))}
            </ul>
          </div>
        </nav>

        <div className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="prose-custom space-y-8">
              {article.content.map((block, index) => renderBlock(block, index))}
            </div>

            <div className="mt-16 pt-10 border-t border-background-200">
              <p className="text-sm text-foreground-500 mb-4 text-center">
                Ready to explore more Vigorbuy Spreadsheet resources?
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Back to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Visit Main Website
                </a>
              </div>
            </div>

            <div className="mt-12 pt-10 border-t border-background-200">
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6 text-center">More Vigorbuy Spreadsheet Guides</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {allArticles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 6)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="group bg-background-100 rounded-lg p-4 border border-background-200 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <span className="text-xs text-primary-500 font-medium">{a.category}</span>
                      <h4 className="text-sm font-semibold text-foreground-800 mt-1.5 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {a.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-2">{a.readTime}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center px-4">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-secondary-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-secondary-500"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-500 mb-6">
              Please select an article from our guides section.
            </p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Back to Homepage
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return <ArticleContent slug={slug} />;
}